import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import type { GuildDetailResponse, UpdateGuildPayload } from '@/lib/api';

interface GuildSettingsFormProps {
  guild: GuildDetailResponse;
  onSave: (input: UpdateGuildPayload) => Promise<void>;
}

export function GuildSettingsForm({ guild, onSave }: GuildSettingsFormProps) {
  const [name, setName] = useState(guild.name);
  const [description, setDescription] = useState(guild.description || '');
  const [visibility, setVisibility] = useState(guild.visibility);
  const [maxMembers, setMaxMembers] = useState(guild.maxMembers?.toString() || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form state when guild prop changes (e.g. after save or navigation)
  useEffect(() => {
    setName(guild.name);
    setDescription(guild.description || '');
    setVisibility(guild.visibility);
    setMaxMembers(guild.maxMembers?.toString() || '');
    setError(null);
  }, [guild.guildId, guild.name, guild.description, guild.visibility, guild.maxMembers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (name.trim().length < 2 || name.trim().length > 100) {
      setError('Name must be 2-100 characters');
      return;
    }
    if (maxMembers) {
      const parsed = parseInt(maxMembers, 10);
      if (isNaN(parsed) || parsed < 1 || !Number.isInteger(parsed)) {
        setError('Max members must be a positive integer');
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        visibility,
        maxMembers: maxMembers ? parseInt(maxMembers, 10) : null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="guild-name">Name</Label>
        <Input
          id="guild-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="guild-description">Description</Label>
        <Textarea
          id="guild-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
          rows={4}
        />
      </div>
      <div className="space-y-2">
        <Label>Visibility</Label>
        <Select value={visibility} onValueChange={(v) => setVisibility(v as 'public' | 'private')}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="public">Public</SelectItem>
            <SelectItem value="private">Private</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="guild-max-members">Max Members (optional)</Label>
        <Input
          id="guild-max-members"
          type="number"
          value={maxMembers}
          onChange={(e) => setMaxMembers(e.target.value)}
          min={1}
          placeholder="No limit"
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" disabled={saving}>
        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
        Save Settings
      </Button>
    </form>
  );
}
